import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Download, Loader2, MapPin, Star, Video } from 'lucide-react';
import toast from 'react-hot-toast';
import { ArticleCard } from './components/ArticleCard';
import { Brand } from './components/Brand';
import { MetaManager } from './components/MetaManager';
import { SiteFooter, SiteHeader } from './components/SiteChrome';
import { articles } from './content/articles';
import { ArticlePage } from './pages/ArticlePage';
import { AboutPage } from './pages/AboutPage';
import { GuidesPage } from './pages/GuidesPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ToolPage, ToolsPage } from './pages/ToolsPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { resolveSiteRoute } from './router';
import { getSeoForRoute } from './seo';
import { consumeToolHandoff } from './toolHandoff';
import { contactPageForRoute, isContactLink, trackContactEvent } from './contactTracking';
import { siteConfig } from './siteConfig';
import './styles.css';

const costFactors = [
  ['Prix et financement', 'Prix, apport, intérêts et frais'],
  ['Dépréciation', 'Valeur probablement perdue à la revente'],
  ['Assurance', 'Prime liée au modèle et à votre profil'],
  ['Entretien et pneus', 'Révisions, pneus et réparations prévisibles'],
  ['Consommation', 'Énergie selon vos trajets réels'],
  ['Taxes et contraintes', 'Fiscalité, stationnement et imprévus'],
];

const method = [
  {
    number: '01',
    title: 'Cadrer votre vie',
    text: 'Budget, trajets, kilométrage, famille, stationnement, horizon de détention et compromis acceptables.',
  },
  {
    number: '02',
    title: 'Ouvrir les pistes',
    text: 'Je confronte les générations, motorisations et niveaux de finition à la réalité de votre projet.',
  },
  {
    number: '03',
    title: 'Faire les comptes',
    text: 'Prix, coût global et propositions de financement déjà reçues sont comparés sur une même durée, avec leurs frais et conditions.',
  },
  {
    number: '04',
    title: 'Prendre une direction',
    text: 'Vous recevez trois à cinq options hiérarchisées, les concessions de chacune et un plan d’action clair.',
  },
];

const deliverables = [
  'Combinaison modèle, génération et motorisation',
  'Comparaison du coût global sur votre durée de détention',
  'Lecture des modes d’acquisition et des propositions de financement reçues',
  'Forces, concessions et cas d’usage',
  'Points de vigilance à contrôler',
  'Si utile, annonces repères et questions à poser',
];

const included = [
  'Échange de cadrage',
  'Shortlist de 3 à 5 options',
  'Dossier argumenté',
  'Lecture des modes d’acquisition et des propositions de financement reçues',
  'Débrief de 30 minutes',
  'Une révision incluse',
];

const nextSteps = [
  ['Vous décrivez votre projet', 'Quelques lignes sur votre budget, vos trajets et vos hésitations suffisent.'],
  ['Je vous réponds sous 72 heures', 'Un retour honnête : l’offre correspond-elle à votre projet, ou non ?'],
  ['Vous décidez de poursuivre', 'Si l’offre vous convient, nous fixons l’échange de cadrage. L’envoi du formulaire ne vous engage à rien.'],
];

const faqs = [
  {
    question: 'Pourquoi payer alors que ChatGPT et Reddit sont gratuits ?',
    answer:
      'Parce que l’information gratuite reste à cadrer, trier, vérifier et pondérer selon votre situation. Auto Sens ne vend pas un accès à des données secrètes : vous déléguez une recherche structurée et obtenez une décision argumentée, assumée et expliquée.',
  },
  {
    question: 'Pouvez-vous comparer achat, crédit, LOA et LLD ?',
    answer:
      'Oui. À partir des propositions que vous avez déjà reçues, le dossier distingue les données contractuelles, les calculs comparatifs et mon avis argumenté sur l’impact de chaque proposition dans le coût global de la voiture. Cet avis ne remplace pas un conseil financier réglementé. Je ne recherche, ne négocie ni ne commercialise aucun financement, et je ne recommande aucun organisme.',
  },
  {
    question: 'Est-ce une inspection mécanique ?',
    answer:
      'Non. La mission intervient en amont pour choisir les bons modèles et identifier les contrôles importants. Elle ne remplace ni un essai, ni un historique vérifié, ni une inspection physique par un professionnel.',
  },
  {
    question: 'Contactez-vous les vendeurs ou les concessionnaires ?',
    answer:
      'Non. Je peux vous proposer des véhicules et, si utile, des annonces repères, puis vous aider à préparer les questions et contrôles. Vous contactez, négociez, essayez et achetez directement : je n’effectue aucune démarche auprès du vendeur ou du concessionnaire. Aucun professionnel de l’automobile ou du financement ne rémunère la recommandation.',
  },
  {
    question: 'Quels projets correspondent à l’offre pilote ?',
    answer:
      'L’offre s’adresse aux particuliers qui recherchent principalement un véhicule d’occasion courant entre 15 000 € et 35 000 €. Les véhicules de collection, imports complexes et modèles très haut de gamme nécessitent un cadrage séparé.',
  },
];

const App = ({ path }: { path?: string }) => {
  const pathname = path ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  const route = useMemo(() => resolveSiteRoute(pathname), [pathname]);
  const seo = useMemo(() => getSeoForRoute(route), [route]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    acquisition: '',
    usage: '',
    consent: false,
  });
  const [contactSource, setContactSource] = useState('formulaire-web');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const formStarted = useRef(false);
  const contactPage = contactPageForRoute(route);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (isContactLink(event.target)) trackContactEvent('cta', contactPage);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [contactPage]);

  useEffect(() => {
    const form = formRef.current;
    if (!form || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      trackContactEvent('form_view', 'home');
      observer.disconnect();
    }, { threshold: 0.2 });
    observer.observe(form);
    return () => observer.disconnect();
  }, [route.kind]);

  const handleFormStart = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackContactEvent('form_start', 'home');
  };

  useEffect(() => {
    if (route.kind !== 'home') return;
    const handoff = consumeToolHandoff();
    if (!handoff) return;
    setContactSource(`outil-${handoff.tool}`);
    setFormData((current) => ({
      ...current,
      usage: [
        `Je viens de terminer l’outil « ${handoff.title} » :`,
        handoff.summary,
        '',
        'Ce que je souhaite préciser :',
      ].join('\n'),
    }));
  }, [route.kind]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const toastId = toast.loading('Envoi de votre demande…');
    const website = new FormData(event.currentTarget).get('website');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          budget: formData.budget,
          acquisition: formData.acquisition || 'À définir',
          usage: formData.usage,
          source: contactSource,
          page: 'home',
          consent: formData.consent,
          website,
        }),
      });

      if (!response.ok) throw new Error('Contact endpoint unavailable');

      const result = await response.json();
      toast.success(
        result.notificationDelivered
          ? `Demande ${result.reference} enregistrée. Un email de confirmation vous a été envoyé.`
          : `Demande ${result.reference} enregistrée. Je reviens vers vous rapidement.`,
        { id: toastId },
      );
      setFormData({ name: '', email: '', phone: '', budget: '', acquisition: '', usage: '', consent: false });
      setContactSource('formulaire-web');
    } catch {
      trackContactEvent('error', 'home');
      toast.error('Impossible d’envoyer la demande pour le moment.', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (route.kind !== 'home') {
    return (
      <div className="autosens-page">
        <MetaManager seo={seo} />
        <SiteHeader path={route.path} />
        {route.kind === 'guides' && <GuidesPage />}
        {route.kind === 'article' && <ArticlePage article={route.article} />}
        {route.kind === 'about' && <AboutPage />}
        {route.kind === 'tools' && <ToolsPage />}
        {route.kind === 'tool' && <ToolPage tool={route.tool} />}
        {route.kind === 'privacy' && <PrivacyPage />}
        {route.kind === 'unsubscribe' && <UnsubscribePage />}
        {route.kind === 'not-found' && <NotFoundPage />}
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="autosens-page">
      <MetaManager seo={seo} />
      <SiteHeader path={route.path} />

      <main id="top">
        <section className="as-hero" aria-labelledby="as-hero-title">
          <div className="as-hero-copy">
            <p className="as-descriptor">Conseiller automobile indépendant · Angers et à distance</p>
            <h1 id="as-hero-title">
              <span>La voiture<br />qui colle à<br />votre vie.</span>
              <strong>Et à votre<br />budget réel.</strong>
            </h1>
            <p className="as-hero-intro">
              Une shortlist personnalisée, des annonces repères lorsqu’elles sont utiles, et
              une lecture des propositions de financement que vous avez déjà reçues — le tout
              fondé sur vos usages, votre budget et les compromis que vous êtes prêt à faire.
            </p>
            <div className="as-hero-actions">
              <a className="as-button as-button-dark" href="#contact">
                Lancer ma recherche
                <ArrowRight size={17} aria-hidden="true" />
              </a>
              <a className="as-link" href="#cout-reel">Voir ce que je compare</a>
            </div>
            <p className="as-hero-reassurance">Réponse sous 72 h · Sans engagement · Aucune commission</p>
            <dl className="as-proof">
              <div><dt>Shortlist</dt><dd>3–5 options</dd></div>
              <div><dt>Lecture</dt><dd>Coût global</dd></div>
              <div><dt>Offre pilote</dt><dd>249 €</dd></div>
            </dl>
          </div>

          <figure className="as-hero-visual">
            <img
              src="/assets/autosens-hero-classic.webp"
              alt="Une voiture familiale argentée sur une vaste aire minérale traversée par une ligne bleue"
              width="1122"
              height="1402"
              loading="eager"
              decoding="async"
            />
            <span className="as-hero-code">AS / 101</span>
            <figcaption>
              <span>Objectif</span>
              <strong>Choisir avant de chercher</strong>
            </figcaption>
          </figure>
        </section>

        <div className="as-principles" aria-label="Principes Auto Sens" data-motion="split">
          <span>Votre usage, pas un stock.</span>
          <span>Votre coût, pas une mensualité.</span>
        </div>

        <section id="cout-reel" className="as-reality" aria-labelledby="cost-title">
          <div className="as-reality-heading" data-motion="wipe">
            <span className="as-kicker">Le vrai budget automobile</span>
            <h2 id="cost-title">Le prix affiché n’est que le début du calcul.</h2>
          </div>
          <div className="as-facts" data-motion="stagger-lines">
            <p>
              Les transports absorbent <strong>12,7&nbsp;%</strong> de la dépense de consommation
              des ménages en 2024. Une voiture engage donc l’un des grands budgets du foyer,
              souvent pendant plusieurs années.
            </p>
            <p>
              Dans le même temps, la LOA représentait près de <strong>87&nbsp;%</strong> des
              opérations de financement spécialisé de voitures neuves recensées en 2024,
              hors prêts personnels. Sa popularité ne signifie pas qu’elle corresponde à tous
              les usages.
            </p>
          </div>
          <div className="as-costs" data-motion="stagger-grid">
            <p>Pour comparer deux voitures correctement, je regarde l’ensemble du coût.</p>
            <ul>
              {costFactors.map(([title, text]) => (
                <li key={title}><strong>{title}</strong><span>{text}</span></li>
              ))}
            </ul>
          </div>
          <p className="as-sources">
            Sources : <a href="https://www.insee.fr/fr/statistiques/8612568" target="_blank" rel="noreferrer">Insee, dépenses des ménages 2024</a>
            {' '}·{' '}<a href="https://asf-france.com/wp-content/uploads/2025/03/Eqpmt-Part-An24.pdf" target="_blank" rel="noreferrer">ASF, financements 2024</a>
            {' '}·{' '}<a href="https://www.inc-conso.fr/content/location-avec-option-dachat-mode-demploi" target="_blank" rel="noreferrer">Institut national de la consommation</a>
          </p>
        </section>

        <section className="as-independent">
          <div className="as-independent-statement" data-motion="quote">
            <span className="as-kicker">Indépendance</span>
            <blockquote>« Je n’ai aucune voiture à placer. »</blockquote>
          </div>
          <div className="as-independent-copy" data-motion="rise">
            <p className="as-independent-lead">Aucune marque. Aucun vendeur. Aucune commission.</p>
            <h2>De l’information, vous en trouverez partout. Ce qui manque, c’est l’arbitrage.</h2>
            <p>
              Pour un particulier, le parallèle le plus simple vient de l’immobilier : réunir
              le regard de l’agent qui cible les biens adaptés, et celui du courtier qui aide à
              décoder les propositions de financement reçues. Côté automobile, je sélectionne
              les véhicules pertinents, peux signaler des annonces et mets en regard les
              propositions que vous me transmettez. J’accompagne votre achat sans intervenir
              dans la transaction : je ne contacte ni vendeur ni concessionnaire ni organisme de
              financement, et je n’engage aucune démarche à votre place.
            </p>
            <div className="as-commitment">
              <span>Mon engagement</span>
              <strong>Vous accompagner dans l’achat, sans intervenir dans la transaction.</strong>
            </div>
            <a className="as-link" href="/a-propos/">Découvrir Enzo et son parcours</a>
          </div>
        </section>

        <section id="methode" className="as-method" aria-labelledby="method-title">
          <div className="as-method-heading" data-motion="split">
            <span className="as-kicker">La méthode</span>
            <h2 id="method-title">La recommandation commence par votre vie, pas par une voiture.</h2>
            <p>
              L’entretien transforme un besoin parfois flou en critères comparables. La
              recherche peut alors être large sans devenir dispersée.
            </p>
          </div>
          <ol className="as-method-list" data-motion="stagger-lines">
            {method.map((step) => (
              <li key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="dossier" className="as-deliverable" aria-labelledby="dossier-title">
          <div className="as-deliverable-copy" data-motion="rise">
            <h2 id="dossier-title">Un dossier fait pour trancher, pas pour impressionner.</h2>
            <p>
              Chaque option est classée, expliquée et replacée dans son contexte. Le dossier
              garde une trace des arbitrages lorsque les annonces et les avis commencent à
              brouiller la décision.
            </p>
            <ul>
              {deliverables.map((item) => (
                <li key={item}><Check size={15} aria-hidden="true" />{item}</li>
              ))}
            </ul>
            <a className="as-button as-button-dark" href="/documents/autosens-exemple-dossier.pdf" download>
              <Download size={17} aria-hidden="true" /> Télécharger un dossier exemple
            </a>
          </div>

          <div className="as-report" aria-label="Aperçu générique du dossier Auto Sens" data-motion="report">
            <div className="as-report-head">
              <div><span>Auto Sens / dossier 101</span><strong>Usage mixte — famille</strong></div>
              <span>25–30 K€</span>
            </div>
            {[
              ['01', 'Meilleur équilibre', 'Option recommandée', '92 %'],
              ['02', 'Choix rationnel', 'Budget préservé', '86 %'],
              ['03', 'Alternative plaisir', 'Compromis assumé', '79 %'],
            ].map(([rank, label, title, score]) => (
              <div className="as-report-row" key={rank}>
                <span>{rank}</span>
                <div><small>{label}</small><strong>{title}</strong></div>
                <b>{score}</b>
              </div>
            ))}
            <div className="as-report-foot">Exemple de structure — données et modèles fictifs</div>
          </div>
        </section>

        <section className="as-home-tools" aria-labelledby="home-tools-title">
          <div className="as-home-tools-heading" data-motion="split">
            <div><span className="as-kicker">Commencer sans engagement</span><h2 id="home-tools-title">Un premier résultat en quelques minutes.</h2></div>
            <p>Les outils restent entièrement accessibles sans e-mail. Ils cadrent une question avant de comparer des modèles ou de demander un accompagnement.</p>
          </div>
          <div className="as-home-tools-list">
            <a href="/outils/diagnostic-projet/"><span>01</span><strong>Diagnostic de projet</strong><small>3 min</small><ArrowRight size={17} /></a>
            <a href="/outils/orientation-motorisation/"><span>02</span><strong>Orientation motorisation</strong><small>4 min</small><ArrowRight size={17} /></a>
            <a href="/outils/comparateur-cout-global/"><span>03</span><strong>Comparateur de coût global</strong><small>6 min</small><ArrowRight size={17} /></a>
          </div>
        </section>

        <section className="as-home-guides" aria-labelledby="home-guides-title">
          <div className="as-home-guides-heading" data-motion="split">
            <div>
              <span className="as-kicker">Conseils automobiles</span>
              <h2 id="home-guides-title">Les sujets à comprendre avant de comparer les annonces.</h2>
            </div>
            <div>
              <p>
                Des guides complets, sourcés et datés pour cadrer un budget, choisir une
                motorisation ou décrypter un financement avant de décider.
              </p>
              <a className="as-link" href="/conseils/">Voir tous les conseils</a>
            </div>
          </div>
          <div className="as-home-guides-list">
            {articles.slice(0, 3).map((article, index) => (
              <ArticleCard key={article.slug} article={article} featured={index === 0} motionIndex={index % 2} />
            ))}
          </div>
        </section>

        <section className="as-offer" aria-labelledby="offer-title">
          <div className="as-offer-price" data-motion="number">
            <span className="as-kicker">Offre pilote</span>
            <div><strong>249</strong><span>€ TTC</span></div>
            <p>Un prix fixe qui rémunère la recherche et le conseil — jamais la transaction.</p>
          </div>
          <div className="as-offer-content" data-motion="rise">
            <h2 id="offer-title">Inclus dans la mission</h2>
            <ul>
              {included.map((item) => (
                <li key={item}><Check size={15} aria-hidden="true" />{item}</li>
              ))}
            </ul>
            <a className="as-button as-button-lime" href="#contact">
              Vérifier si l’offre me correspond
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </div>
        </section>

        <section id="zone" className="as-area" aria-labelledby="area-title">
          <div className="as-area-heading" data-motion="split">
            <span className="as-kicker">Où j’interviens</span>
            <h2 id="area-title">Conseil automobile à Angers et partout en France à distance.</h2>
            <p>
              La mission repose sur l’échange et la recherche, pas sur une visite du véhicule :
              elle fonctionne aussi bien en face à face qu’en visioconférence.
            </p>
          </div>
          <dl className="as-area-list" data-motion="stagger-lines">
            <div>
              <MapPin size={21} aria-hidden="true" />
              <dt>Dans la région d’Angers</dt>
              <dd>Je peux me déplacer pour l’échange de cadrage lorsque le projet et le rendez-vous le justifient.</dd>
            </div>
            <div>
              <Video size={21} aria-hidden="true" />
              <dt>Ailleurs en France</dt>
              <dd>Cadrage, présentation du dossier et débrief se font par téléphone ou en visioconférence ; le dossier vous est remis en PDF.</dd>
            </div>
          </dl>
        </section>

        <section id="questions" className="as-faq" aria-labelledby="faq-title">
          <div className="as-faq-heading" data-motion="wipe">
            <span className="as-kicker">FAQ</span>
            <h2 id="faq-title">Ce que fait Auto Sens. Et ce que le service ne prétend pas faire.</h2>
          </div>
          <div className="as-faq-list" data-motion="stagger-lines">
            {faqs.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}<span aria-hidden="true">+</span></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section id="contact" className="as-contact" aria-labelledby="contact-title">
          <div className="as-contact-copy" data-motion="wipe">
            <span className="as-kicker">Premier échange</span>
            <h2 id="contact-title">Décrivez la voiture qu’il vous faut. Même si vous ne la connaissez pas encore.</h2>
            <p>
              Quelques lignes suffisent. Vous recevrez d’abord un retour honnête sur
              l’adéquation de l’offre à votre projet sous 72 heures. Pour faire le point sur votre besoin,
              je peux me déplacer dans la région d’Angers ; sinon, nous échangeons par
              téléphone ou en visioconférence.
            </p>
            <ol className="as-contact-steps">
              {nextSteps.map(([title, text], index) => (
                <li key={title}><span>{String(index + 1).padStart(2, '0')}</span><div><strong>{title}</strong><p>{text}</p></div></li>
              ))}
            </ol>
            <ul className="as-contact-trust">
              <li><Check size={15} aria-hidden="true" />Aucune rémunération d’une marque, d’un vendeur ou d’un financement</li>
              <li><Check size={15} aria-hidden="true" />Vos informations servent uniquement à répondre à votre demande</li>
              <li><Download size={15} aria-hidden="true" /><a href="/documents/autosens-exemple-dossier.pdf" download>Voir un dossier exemple (cas fictif)</a></li>
              <li><Star size={15} aria-hidden="true" /><a href={siteConfig.googleBusinessUrl} target="_blank" rel="noreferrer">Auto Sens sur Google</a></li>
            </ul>
            <div className="as-contact-signature"><Brand compact /><span>Votre usage.<br />Votre budget réel.</span></div>
          </div>

          <form ref={formRef} className="as-form" onSubmit={handleSubmit} onFocus={handleFormStart} data-motion="form">
            <label className="as-honeypot" aria-hidden="true">Votre site web<input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>
            <label>Votre nom<input type="text" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} required autoComplete="name" /></label>
            <label>Votre email<input type="email" value={formData.email} onChange={(event) => setFormData({ ...formData, email: event.target.value })} required autoComplete="email" /></label>
            <label>Votre téléphone <span className="as-optional">facultatif</span><input type="tel" value={formData.phone} onChange={(event) => setFormData({ ...formData, phone: event.target.value })} autoComplete="tel" /></label>
            <label>Budget approximatif<input type="text" value={formData.budget} onChange={(event) => setFormData({ ...formData, budget: event.target.value })} placeholder="Ex. 25 000 €" required /></label>
            <label>Mode d’acquisition envisagé<select value={formData.acquisition} onChange={(event) => setFormData({ ...formData, acquisition: event.target.value })}>
              <option value="">Je ne sais pas encore</option><option value="Achat comptant">Achat comptant</option><option value="Crédit">Crédit</option><option value="LOA">LOA</option><option value="LLD">LLD</option>
            </select></label>
            <label className="as-form-wide">Usage, contraintes, hésitations<textarea value={formData.usage} onChange={(event) => setFormData({ ...formData, usage: event.target.value })} rows={5} placeholder="Ex. 20 000 km/an, deux enfants, trajets mixtes, hésitation hybride/essence…" required /></label>
            <label className="as-form-consent as-form-wide"><input type="checkbox" checked={formData.consent} onChange={(event) => setFormData({ ...formData, consent: event.target.checked })} required /><span>J’accepte que ces informations soient utilisées uniquement pour répondre à ma demande et préparer, si je poursuis, mon dossier Auto Sens.</span></label>
            <button className="as-button as-button-lime as-form-wide" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <><Loader2 className="as-spinner" size={17} aria-hidden="true" />Envoi…</> : <>Envoyer ma demande<ArrowRight size={17} aria-hidden="true" /></>}
            </button>
          </form>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default App;
